import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCachedAccessibleStores } from '@/lib/runtimeMetaCache';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  getOrdersPaginatedLite,
  updateOrderStatus,
  deleteOrder,
  bulkDeleteOrders,
  getWooOrdersPreview,
  importWooOrder,
  runAdminWooReconcileDryRun,
  runAdminWooReconcileApply,
  runCustomerMasterBackfill,
} from '@/lib/api';
import { formatStoreName } from '@/lib/storeUtils';
import { useAuth } from '@/context/AuthContext';
import { 
  Plus, Search, MoreVertical, ShoppingBag, Trash2, Edit2,
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp, ArrowUpDown, Filter, X, Users, Link2, DownloadCloud, Loader2, Copy, Check, FileText, ExternalLink
} from 'lucide-react';
import { format } from 'date-fns';
import { getApiBaseUrl } from '@/lib/apiBaseUrl';
import { formatDateWord } from '@/lib/dateFormat';

const statusColors = {
  enquiry: 'bg-slate-600/80 text-white border-slate-600',
  pending: 'bg-amber-600/80 text-white border-amber-600',
  processing: 'bg-blue-600/80 text-white border-blue-600',
  pending_payment: 'bg-orange-500/80 text-white border-orange-500',
  completed: 'bg-emerald-600/80 text-white border-emerald-600',
  cancelled: 'bg-red-900/80 text-white border-red-900',
  refunded: 'bg-gray-700/80 text-white border-gray-700',
};

const statusLabels = {
  enquiry: 'Enquiry',
  processing: 'Processing',
  pending_payment: 'Pending Payment',
  completed: 'Completed',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
};

const API_URL = getApiBaseUrl();

const formatDateInputValue = (dateValue) => format(dateValue, 'yyyy-MM-dd');

const ORDER_DATE_RANGE_OPTIONS = [
  { value: 'custom', label: 'Custom (From / To)' },
  { value: 'year_to_date', label: 'Year to Date' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'last_12_months', label: 'Last 12 Months' },
  { value: '__all__', label: 'All Time' },
];

const DEFAULT_WOO_STATUS_MAPPING = {
  pending: 'pending_payment',
  processing: 'processing',
  'on-hold': 'pending_payment',
  completed: 'completed',
  cancelled: 'cancelled',
  refunded: 'refunded',
  failed: 'cancelled',
};

const WOO_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'on-hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'failed', label: 'Failed' },
];

const INTERNAL_STATUS_OPTIONS = [
  { value: 'enquiry', label: 'Enquiry' },
  { value: 'processing', label: 'Processing' },
  { value: 'pending_payment', label: 'Pending Payment' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'refunded', label: 'Refunded' },
];

const WOO_IMPORT_CHUNK_SIZE = 6;
const WOO_IMPORT_CONCURRENCY = 3;
const WOO_RISKY_MAPPINGS_WARNING_ONLY = true;
const SHOW_WOO_RECONCILE_UI = false;

const WOO_SKIP_REASON_LABELS = {
  already_imported: 'Already imported (duplicate invoice)',
  no_line_items_selected: 'No line items selected',
  unmapped_fields: 'Contains unmapped Woo fields',
  missing_product_mapping: 'Contains line items without product mapping',
  risky_field_mappings: 'Contains potentially incorrect auto-mappings',
};

const SortButton = ({ field, children, onSort, activeField }) => (
  <button
    className="flex items-center gap-1 hover:text-foreground transition-colors"
    onClick={() => onSort(field)}
  >
    {children}
    <ArrowUpDown className={`w-3 h-3 ${activeField === field ? 'text-primary' : 'opacity-50'}`} />
  </button>
);

const OrdersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalOrders, setTotalOrders] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(75);
  
  // Stores list - fetched dynamically based on user access
  const [stores, setStores] = useState([]);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('__all__');
  const [storeFilter, setStoreFilter] = useState('__all__');
  const [dataTierFilter, setDataTierFilter] = useState('hot');
  const [orderDateRangeFilter, setOrderDateRangeFilter] = useState('custom');
  const [orderDateFrom, setOrderDateFrom] = useState('');
  const [orderDateTo, setOrderDateTo] = useState('');
  const [sortBy, setSortBy] = useState('order_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  const fetchAbortRef = useRef(null);
  const fetchSeqRef = useRef(0);
  
  // Selection
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [notesDialogOrder, setNotesDialogOrder] = useState(null);
  const [copiedOrderId, setCopiedOrderId] = useState(null);

  // WooCommerce import
  const [wooImportOpen, setWooImportOpen] = useState(false);
  const [wooLoading, setWooLoading] = useState(false);
  const [wooImporting, setWooImporting] = useState(false);
  const [wooOrders, setWooOrders] = useState([]);
  const [wooSearchTerm, setWooSearchTerm] = useState('');
  const [wooStatusFilter, setWooStatusFilter] = useState('all');
  const [wooDateFrom, setWooDateFrom] = useState(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return formatDateInputValue(firstDay);
  });
  const [wooDateTo, setWooDateTo] = useState(() => formatDateInputValue(new Date()));
  const [wooSelectedOrderId, setWooSelectedOrderId] = useState(null);
  const [wooSelectedOrderIds, setWooSelectedOrderIds] = useState([]);
  const [wooSelectedLineItemsByOrder, setWooSelectedLineItemsByOrder] = useState({});
  const [wooLineItemMappingsByOrder, setWooLineItemMappingsByOrder] = useState({});
  const [wooStatusMapping, setWooStatusMapping] = useState(DEFAULT_WOO_STATUS_MAPPING);
  const [wooStoreSelection, setWooStoreSelection] = useState('');
  const [wooShowOnlyUnmapped, setWooShowOnlyUnmapped] = useState(false);
  const [wooSummaryModalOpen, setWooSummaryModalOpen] = useState(false);
  const [wooImportSummary, setWooImportSummary] = useState(null);

  // Admin Woo Reconcile
  const canUseWooReconcile = ['owner', 'admin'].includes(user?.role);
  const canRunCustomerMasterRepair = ['owner', 'admin'].includes(user?.role);
  const [wooReconcileOrderNumbersInput, setWooReconcileOrderNumbersInput] = useState('');
  const [wooReconcileIncludeDetected, setWooReconcileIncludeDetected] = useState(true);
  const [wooReconcileScope, setWooReconcileScope] = useState('all');
  const [wooReconcileMaxOrders, setWooReconcileMaxOrders] = useState('120');
  const [wooReconcileLoading, setWooReconcileLoading] = useState(false);
  const [wooReconcileApplying, setWooReconcileApplying] = useState(false);
  const [wooReconcileResult, setWooReconcileResult] = useState(null);
  const [wooReconcileExpandedLineItems, setWooReconcileExpandedLineItems] = useState({});
  const [customerRepairDialogOpen, setCustomerRepairDialogOpen] = useState(false);
  const [customerRepairConfirmText, setCustomerRepairConfirmText] = useState('');
  const [customerRepairRunning, setCustomerRepairRunning] = useState(false);

  // Fetch accessible stores on mount
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const storesData = await getCachedAccessibleStores();
        setStores(Array.isArray(storesData) ? storesData : []);
      } catch (error) {
        console.error('Failed to fetch stores:', error);
      }
    };
    fetchStores();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    return () => {
      if (fetchAbortRef.current) {
        fetchAbortRef.current.abort();
      }
    };
  }, []);

  const fetchOrders = useCallback(async () => {
    const requestId = fetchSeqRef.current + 1;
    fetchSeqRef.current = requestId;

    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }

    const abortController = new AbortController();
    fetchAbortRef.current = abortController;

    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
        tier: dataTierFilter,
      };
      
      if (debouncedSearchTerm.length >= 2) params.search = debouncedSearchTerm;
      
      if (statusFilter && statusFilter !== '__all__') {
        params.status = statusFilter;
      }
      
      if (storeFilter && storeFilter !== '__all__') params.store = storeFilter;

      const toStartOfDayIso = (dateValue) => (dateValue ? new Date(`${dateValue}T00:00:00`).toISOString() : null);
      const toEndOfDayIso = (dateValue) => (dateValue ? new Date(`${dateValue}T23:59:59.999`).toISOString() : null);

      const now = new Date();
      let resolvedDateFrom = null;
      let resolvedDateTo = null;

      if (orderDateRangeFilter === 'custom') {
        resolvedDateFrom = toStartOfDayIso(orderDateFrom);
        resolvedDateTo = toEndOfDayIso(orderDateTo);
      } else if (orderDateRangeFilter === 'year_to_date') {
        const start = new Date(now.getFullYear(), 0, 1);
        resolvedDateFrom = start.toISOString();
        resolvedDateTo = now.toISOString();
      } else if (orderDateRangeFilter === 'last_year') {
        const year = now.getFullYear() - 1;
        const start = new Date(year, 0, 1, 0, 0, 0, 0);
        const end = new Date(year, 11, 31, 23, 59, 59, 999);
        resolvedDateFrom = start.toISOString();
        resolvedDateTo = end.toISOString();
      } else if (orderDateRangeFilter === 'last_12_months') {
        const start = new Date(now);
        start.setFullYear(start.getFullYear() - 1);
        start.setHours(0, 0, 0, 0);
        resolvedDateFrom = start.toISOString();
        resolvedDateTo = now.toISOString();
      }

      if (resolvedDateFrom) params.date_from = resolvedDateFrom;
      if (resolvedDateTo) params.date_to = resolvedDateTo;
      
      const response = await getOrdersPaginatedLite(params, { signal: abortController.signal });

      if (requestId !== fetchSeqRef.current) {
        return;
      }

      setOrders(response.data.orders);
      setTotalOrders(response.data.total);
      setTotalPages(response.data.total_pages);
      setSelectedOrders((current) => current.filter((id) => response.data.orders.some((order) => order.order_id === id)));
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        return;
      }
      toast.error('Failed to fetch orders');
    } finally {
      if (fetchAbortRef.current === abortController) {
        fetchAbortRef.current = null;
      }
      if (requestId === fetchSeqRef.current) {
        setLoading(false);
      }
    }
  }, [currentPage, pageSize, debouncedSearchTerm, statusFilter, storeFilter, dataTierFilter, sortBy, sortOrder, orderDateRangeFilter, orderDateFrom, orderDateTo]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter, storeFilter, dataTierFilter, orderDateRangeFilter, orderDateFrom, orderDateTo]);

  useEffect(() => {
    if (stores.length === 1) {
      setWooStoreSelection(stores[0].store_id);
    }
  }, [stores]);

  const selectedWooOrder = wooOrders.find(order => order.woo_order_id === wooSelectedOrderId) || null;
  const selectedWooOrderLineItemIds = wooSelectedLineItemsByOrder[wooSelectedOrderId] || [];

  const buildDefaultMappingForOrder = (order) => {
    const mapping = {};
    (order?.line_items || []).forEach(item => {
      mapping[item.line_item_id] = { ...(item.auto_mapping || {}) };
    });
    return mapping;
  };

  const buildDefaultSelectedLineItemsForOrder = (order) => (order?.line_items || []).map(item => item.line_item_id);

  const getLineItemMapping = (orderId, lineItemId) => {
    return (wooLineItemMappingsByOrder[orderId] || {})[lineItemId] || {};
  };

  function getUnmappedWooFieldsForLineItem(lineItem, orderId = wooSelectedOrderId) {
    const mapping = getLineItemMapping(orderId, lineItem.line_item_id);
    return (lineItem.woo_fields || []).filter(field => {
      const mapped = mapping[field.field_ref];
      return !mapped || mapped === '__unmapped__';
    });
  }

  function getRiskyWooFieldsForLineItem(lineItem) {
    const riskyRefs = new Set(lineItem?.risky_mapping_woo_field_refs || []);
    return (lineItem?.woo_fields || []).filter(field => riskyRefs.has(field.field_ref));
  }

  const getSelectedLineItemsForOrder = (order) => {
    const selectedLineItemIds = wooSelectedLineItemsByOrder[order.woo_order_id] || [];
    return (order.line_items || []).filter(item => selectedLineItemIds.includes(item.line_item_id));
  };

  const getUnmappedCountForOrder = (order) => {
    return getSelectedLineItemsForOrder(order).reduce((sum, item) => {
      return sum + getUnmappedWooFieldsForLineItem(item, order.woo_order_id).length;
    }, 0);
  };

  const getRiskyMappingCountForOrder = (order) => {
    return getSelectedLineItemsForOrder(order).reduce((sum, item) => {
      return sum + getRiskyWooFieldsForLineItem(item).length;
    }, 0);
  };

  const fetchWooPreview = useCallback(async () => {
    setWooLoading(true);
    try {
      const params = { page: 1, per_page: 50, fetch_all: true };
      if (wooSearchTerm) params.search = wooSearchTerm;
      if (wooStatusFilter && wooStatusFilter !== 'all') params.status = wooStatusFilter;

      const parsedFrom = wooDateFrom ? new Date(`${wooDateFrom}T00:00:00`) : null;
      const parsedTo = wooDateTo ? new Date(`${wooDateTo}T23:59:59.999`) : null;

      if (parsedFrom && Number.isNaN(parsedFrom.getTime())) {
        toast.error('Please enter a valid From date');
        return;
      }
      if (parsedTo && Number.isNaN(parsedTo.getTime())) {
        toast.error('Please enter a valid To date');
        return;
      }

      if (parsedFrom && parsedTo && parsedFrom > parsedTo) {
        toast.error('From date must be earlier than To date');
        return;
      }

      if (parsedFrom) {
        params.after = parsedFrom.toISOString();
      }
      if (parsedTo) {
        params.before = parsedTo.toISOString();
      }

      const response = await getWooOrdersPreview(params);
      const items = response.data?.items || [];
      setWooOrders(items);

      setWooSelectedOrderId((previous) => {
        if (previous && items.some(order => order.woo_order_id === previous)) {
          return previous;
        }
        return items[0]?.woo_order_id ?? null;
      });

      setWooSelectedOrderIds((previous) => {
        const visibleIds = new Set(items.map(order => order.woo_order_id));
        const retained = previous.filter(id => visibleIds.has(id));
        if (retained.length > 0) return retained;
        return items
          .filter(order => !order.already_imported)
          .map(order => order.woo_order_id);
      });

      setWooSelectedLineItemsByOrder((previous) => {
        const next = {};
        items.forEach(order => {
          const previousSelection = previous[order.woo_order_id];
          next[order.woo_order_id] = (previousSelection && previousSelection.length > 0)
            ? previousSelection
            : buildDefaultSelectedLineItemsForOrder(order);
        });
        return next;
      });

      setWooLineItemMappingsByOrder((previous) => {
        const next = {};
        items.forEach(order => {
          next[order.woo_order_id] = previous[order.woo_order_id] || buildDefaultMappingForOrder(order);
        });
        return next;
      });
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to fetch WooCommerce orders');
    } finally {
      setWooLoading(false);
    }
  }, [wooSearchTerm, wooStatusFilter, wooDateFrom, wooDateTo]);

  const openWooImportModal = async () => {
    setWooImportOpen(true);
    setWooSelectedOrderId(null);
    setWooSelectedOrderIds([]);
    setWooSelectedLineItemsByOrder({});
    setWooLineItemMappingsByOrder({});
    setWooShowOnlyUnmapped(false);
    setWooSummaryModalOpen(false);
    setWooImportSummary(null);
    await fetchWooPreview();
  };

  const handleSelectWooOrder = (order) => {
    setWooSelectedOrderId(order.woo_order_id);
  };

  const toggleWooOrderSelection = (wooOrderId) => {
    setWooSelectedOrderIds((previous) => (
      previous.includes(wooOrderId)
        ? previous.filter(id => id !== wooOrderId)
        : [...previous, wooOrderId]
    ));
  };

  const selectAllVisibleWooOrders = () => {
    setWooSelectedOrderIds(wooOrders.map(order => order.woo_order_id));
  };

  const clearWooOrderSelection = () => {
    setWooSelectedOrderIds([]);
  };

  const toggleWooLineItem = (lineItemId) => {
    if (!wooSelectedOrderId) return;
    setWooSelectedLineItemsByOrder((previous) => {
      const existing = previous[wooSelectedOrderId] || [];
      const nextSelection = existing.includes(lineItemId)
        ? existing.filter(id => id !== lineItemId)
        : [...existing, lineItemId];
      return {
        ...previous,
        [wooSelectedOrderId]: nextSelection,
      };
    });
  };

  const updateWooFieldMapping = (lineItemId, fieldRef, optionId) => {
    if (!wooSelectedOrderId) return;
    setWooLineItemMappingsByOrder((previous) => {
      const orderMappings = { ...(previous[wooSelectedOrderId] || {}) };
      const currentLineItemMapping = { ...(orderMappings[lineItemId] || {}) };
      if (!optionId || optionId === '__unmapped__') {
        delete currentLineItemMapping[fieldRef];
      } else {
        currentLineItemMapping[fieldRef] = optionId;
      }
      orderMappings[lineItemId] = currentLineItemMapping;
      return {
        ...previous,
        [wooSelectedOrderId]: orderMappings,
      };
    });
  };

  const updateWooStatusMapping = (wooStatus, internalStatus) => {
    setWooStatusMapping(previous => ({
      ...previous,
      [wooStatus]: internalStatus,
    }));
  };

  const chunkArray = (items, chunkSize) => {
    const chunks = [];
    for (let index = 0; index < items.length; index += chunkSize) {
      chunks.push(items.slice(index, index + chunkSize));
    }
    return chunks;
  };

  const buildWooImportSummary = () => {
    const selectedOrders = wooOrders.filter(order => wooSelectedOrderIds.includes(order.woo_order_id));
    const skippedOrders = [];
    const importableOrders = [];
    const warningOrders = [];

    selectedOrders.forEach((order) => {
      const selectedLineItemIds = wooSelectedLineItemsByOrder[order.woo_order_id] || [];
      const selectedLineItems = (order.line_items || []).filter(item => selectedLineItemIds.includes(item.line_item_id));
      const reasons = [];

      if (order.already_imported) {
        reasons.push({ code: 'already_imported', detail: WOO_SKIP_REASON_LABELS.already_imported });
      }

      if (selectedLineItemIds.length === 0) {
        reasons.push({ code: 'no_line_items_selected', detail: WOO_SKIP_REASON_LABELS.no_line_items_selected });
      }

      const unmappedItems = selectedLineItems
        .map(item => ({
          item,
          unmappedFields: getUnmappedWooFieldsForLineItem(item, order.woo_order_id),
        }))
        .filter(entry => entry.unmappedFields.length > 0);

      if (unmappedItems.length > 0) {
        const unmappedFieldCount = unmappedItems.reduce((sum, entry) => sum + entry.unmappedFields.length, 0);
        reasons.push({
          code: 'unmapped_fields',
          detail: `${WOO_SKIP_REASON_LABELS.unmapped_fields} (${unmappedFieldCount} fields)`,
        });
      }

      const missingProductItems = selectedLineItems
        .filter(item => !item.matched_product_id)
        .map(item => item.name)
        .filter(Boolean);

      if (missingProductItems.length > 0) {
        reasons.push({
          code: 'missing_product_mapping',
          detail: `${WOO_SKIP_REASON_LABELS.missing_product_mapping}: ${missingProductItems.slice(0, 2).join(', ')}${missingProductItems.length > 2 ? '…' : ''}`,
        });
      }

      const riskyItems = selectedLineItems
        .map(item => ({
          item,
          riskyFields: getRiskyWooFieldsForLineItem(item),
        }))
        .filter(entry => entry.riskyFields.length > 0);

      if (riskyItems.length > 0) {
        const riskyFieldCount = riskyItems.reduce((sum, entry) => sum + entry.riskyFields.length, 0);
        warningOrders.push({
          woo_order_id: order.woo_order_id,
          woo_order_number: order.woo_order_number,
          invoice_number: order.invoice_number,
          selected_line_items_count: selectedLineItems.length,
          reasons: [{
            code: 'risky_field_mappings',
            detail: `${WOO_SKIP_REASON_LABELS.risky_field_mappings} (${riskyFieldCount} fields)`,
          }],
        });
      }

      const blockingReasons = (reasons || []).filter(reason => {
        if (!WOO_RISKY_MAPPINGS_WARNING_ONLY) return true;
        return reason?.code !== 'risky_field_mappings';
      });

      if (blockingReasons.length > 0) {
        skippedOrders.push({
          woo_order_id: order.woo_order_id,
          woo_order_number: order.woo_order_number,
          invoice_number: order.invoice_number,
          selected_line_items_count: selectedLineItems.length,
          reasons: blockingReasons,
        });
      } else {
        importableOrders.push(order);
      }
    });

    return {
      selected_count: selectedOrders.length,
      importable_count: importableOrders.length,
      skipped_count: skippedOrders.length,
      warning_count: warningOrders.length,
      skipped_orders: skippedOrders,
      warning_orders: warningOrders,
      importable_orders: importableOrders,
    };
  };

  const openWooImportSummary = () => {
    if (wooSelectedOrderIds.length === 0) {
      toast.error('Select at least one WooCommerce order to import');
      return;
    }
    if (stores.length > 1 && !wooStoreSelection) {
      toast.error('Select a store for imported orders');
      return;
    }

    const summary = buildWooImportSummary();
    setWooImportSummary(summary);
    setWooSummaryModalOpen(true);
  };

  const runConcurrentWooImport = async (ordersToImport) => {
    const orderPayloads = ordersToImport.map(order => ({
      woo_order_id: order.woo_order_id,
      selected_line_item_ids: wooSelectedLineItemsByOrder[order.woo_order_id] || [],
      mapping_by_line_item: wooLineItemMappingsByOrder[order.woo_order_id] || {},
    }));

    const chunks = chunkArray(orderPayloads, WOO_IMPORT_CHUNK_SIZE);
    const workerCount = Math.min(WOO_IMPORT_CONCURRENCY, chunks.length);

    const aggregate = {
      imported_count: 0,
      skipped_count: 0,
      error_count: 0,
      results: {
        imported: [],
        skipped: [],
        errors: [],
      },
    };

    let cursor = 0;
    const runWorker = async () => {
      while (true) {
        const chunkIndex = cursor;
        cursor += 1;
        if (chunkIndex >= chunks.length) break;

        const chunk = chunks[chunkIndex];
        try {
          const response = await importWooOrder({
            store: wooStoreSelection || undefined,
            status_mapping: wooStatusMapping,
            orders: chunk,
          });
          const data = response.data || {};

          if (data.mode === 'batch') {
            aggregate.imported_count += Number(data.imported_count || 0);
            aggregate.skipped_count += Number(data.skipped_count || 0);
            aggregate.error_count += Number(data.error_count || 0);
            aggregate.results.imported.push(...(data?.results?.imported || []));
            aggregate.results.skipped.push(...(data?.results?.skipped || []));
            aggregate.results.errors.push(...(data?.results?.errors || []));
          }
        } catch (error) {
          const detail = error?.response?.data?.detail || error?.message || 'WooCommerce import failed';
          aggregate.error_count += chunk.length;
          chunk.forEach((orderPayload) => {
            aggregate.results.errors.push({
              woo_order_id: String(orderPayload.woo_order_id || ''),
              detail,
            });
          });
        }
      }
    };

    await Promise.all(Array.from({ length: workerCount }, () => runWorker()));
    return aggregate;
  };

  const handleImportWooOrder = async () => {
    if (!wooImportSummary) {
      toast.error('Review summary before importing');
      return;
    }

    if (wooImportSummary.importable_count === 0) {
      toast.info('No importable orders in selected set. Review skip reasons.');
      return;
    }

    setWooImporting(true);
    try {
      const result = await runConcurrentWooImport(wooImportSummary.importable_orders);

      if (result.imported_count > 0) {
        toast.success(`Imported ${result.imported_count} Woo order(s)`);
        fetchOrders();
      }

      if (wooImportSummary.skipped_count > 0) {
        toast.info(`Pre-check skipped ${wooImportSummary.skipped_count} order(s). Review summary for reasons.`);
      }

      if (result.skipped_count > 0) {
        toast.info(`Skipped ${result.skipped_count} duplicate/already-imported order(s) during import.`);
      }

      if (result.error_count > 0) {
        const firstError = result?.results?.errors?.[0];
        const detailMessage = typeof firstError?.detail === 'string'
          ? firstError.detail
          : firstError?.detail?.message || 'Some orders failed to import';
        if (result.imported_count > 0) {
          toast.warning(`Imported ${result.imported_count} order(s), but ${result.error_count} failed. First issue: ${detailMessage}`);
        } else {
          toast.error(detailMessage);
        }
      }

      await fetchWooPreview();
      setWooSummaryModalOpen(false);
      if (result.error_count === 0) {
        setWooImportOpen(false);
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.message) {
        toast.error(detail.message);
      } else {
        toast.error(detail || 'WooCommerce import failed');
      }
    } finally {
      setWooImporting(false);
    }
  };

  const parseWooReconcileOrderNumbers = (rawValue) => {
    return Array.from(
      new Set(
        String(rawValue || '')
          .split(/[\n,\s]+/)
          .map((value) => value.trim().toUpperCase())
          .filter(Boolean)
      )
    );
  };

  const buildWooReconcilePayload = () => {
    const normalizedMaxOrders = Number.parseInt(wooReconcileMaxOrders, 10);
    return {
      order_numbers: parseWooReconcileOrderNumbers(wooReconcileOrderNumbersInput),
      include_detected_mismatches: wooReconcileIncludeDetected,
      mismatch_scope: wooReconcileScope,
      max_orders: Number.isFinite(normalizedMaxOrders) ? normalizedMaxOrders : 120,
    };
  };

  const handleWooReconcileDryRun = async () => {
    if (!canUseWooReconcile) return;

    setWooReconcileLoading(true);
    try {
      const payload = buildWooReconcilePayload();
      const response = await runAdminWooReconcileDryRun(payload);
      setWooReconcileResult(response.data);
      setWooReconcileExpandedLineItems({});
      toast.success(`Woo reconcile dry-run complete. ${response.data?.orders_with_changes || 0} order(s) need updates.`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Woo reconcile dry-run failed');
    } finally {
      setWooReconcileLoading(false);
    }
  };

  const handleWooReconcileApply = async () => {
    if (!canUseWooReconcile) return;

    setWooReconcileApplying(true);
    try {
      const payload = buildWooReconcilePayload();
      const response = await runAdminWooReconcileApply(payload);
      setWooReconcileResult(response.data);
      setWooReconcileExpandedLineItems({});
      toast.success(
        `Woo reconcile applied. Updated ${response.data?.orders_updated || 0} order(s), ${response.data?.line_items_updated || 0} line item(s).`
      );
      fetchOrders();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Woo reconcile apply failed');
    } finally {
      setWooReconcileApplying(false);
    }
  };

  const buildWooReconcileLineItemKey = (orderNumber, lineItem) => {
    return `${orderNumber || 'order'}-${lineItem?.line_item_id || 'line'}-${lineItem?.source_wc_line_item_id || 'wc'}`;
  };

  const toggleWooReconcileLineItemExpand = (orderNumber, lineItem) => {
    const key = buildWooReconcileLineItemKey(orderNumber, lineItem);
    setWooReconcileExpandedLineItems((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await updateOrderStatus(orderId, newStatus);
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (orderId) => {
    try {
      await deleteOrder(orderId);
      toast.success('Order deleted');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to delete order');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await bulkDeleteOrders(selectedOrders);
      toast.success(`Deleted ${selectedOrders.length} orders`);
      setSelectedOrders([]);
      setDeleteDialogOpen(false);
      fetchOrders();
    } catch (error) {
      toast.error('Failed to delete orders');
    }
  };

  const toggleSelectAll = () => {
    if (selectedOrders.length === orders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders.map(o => o.order_id));
    }
  };

  const toggleSelectOrder = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('__all__');
    setStoreFilter('__all__');
    setOrderDateRangeFilter('custom');
    setOrderDateFrom('');
    setOrderDateTo('');
  };

  const handleRunCustomerMasterRepair = async () => {
    if (!canRunCustomerMasterRepair) return;
    setCustomerRepairRunning(true);
    try {
      const response = await runCustomerMasterBackfill();
      const summary = response?.data?.summary || {};
      toast.success(
        `Repair complete: Orders ${summary.orders_updated || 0}, Garments ${summary.garments_updated || 0}, Fabric ${summary.fabric_orders_updated || 0}`
      );
      setCustomerRepairDialogOpen(false);
      setCustomerRepairConfirmText('');
      fetchOrders();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Customer master repair failed');
    } finally {
      setCustomerRepairRunning(false);
    }
  };

  const openNotesDialog = (order) => {
    setNotesDialogOrder(order);
    setNotesDialogOpen(true);
  };

  const handleCopyOrderNumber = async (order) => {
    try {
      await navigator.clipboard.writeText(order.order_number || '');
      setCopiedOrderId(order.order_id);
      toast.success('Order number copied');
      window.setTimeout(() => {
        setCopiedOrderId((current) => (current === order.order_id ? null : current));
      }, 1200);
    } catch (error) {
      toast.error('Failed to copy order number');
    }
  };

  const hasActiveFilters = searchTerm || (statusFilter && statusFilter !== '__all__') || (storeFilter && storeFilter !== '__all__') || (dataTierFilter && dataTierFilter !== 'hot') || (orderDateRangeFilter && orderDateRangeFilter !== '__all__' && orderDateRangeFilter !== 'custom') || (orderDateRangeFilter === 'custom' && (orderDateFrom || orderDateTo));

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <div className="space-y-6 p-3" data-testid="orders-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Orders
          </h1>
          <p className="text-muted-foreground text-sm">
            {totalOrders} total orders
          </p>
        </div>
        <div className="flex gap-3">
          {selectedOrders.length > 0 && (
            <Button 
              variant="destructive"
              onClick={() => setDeleteDialogOpen(true)}
              data-testid="bulk-delete-button"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete ({selectedOrders.length})
            </Button>
          )}
          <Button
            variant="outline"
            onClick={openWooImportModal}
            data-testid="open-woo-import-button"
          >
            <DownloadCloud className="w-4 h-4 mr-2" />
            Import Woo Orders
          </Button>
          {canRunCustomerMasterRepair && (
            <Button
              variant="outline"
              onClick={() => setCustomerRepairDialogOpen(true)}
              data-testid="orders-customer-master-repair-button"
            >
              Repair Customer Data
            </Button>
          )}
          <Button 
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={() => navigate('/orders/new')}
            data-testid="new-order-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Order
          </Button>
        </div>
      </div>

      {SHOW_WOO_RECONCILE_UI && canUseWooReconcile && (
        <Card className="border-border bg-card shadow-sm rounded-xl" data-testid="woo-reconcile-admin-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base" data-testid="woo-reconcile-admin-title">Woo Reconcile (Admin)</CardTitle>
            <p className="text-sm text-muted-foreground" data-testid="woo-reconcile-admin-description">
              Repair historical Woo customization mappings with safe dry-run and apply controls.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground" data-testid="woo-reconcile-order-input-label">
                  Explicit order numbers (comma/newline separated)
                </label>
                <Textarea
                  value={wooReconcileOrderNumbersInput}
                  onChange={(event) => setWooReconcileOrderNumbersInput(event.target.value)}
                  placeholder="PER11831372\nPER8091337"
                  className="min-h-[92px]"
                  data-testid="woo-reconcile-order-numbers-input"
                />
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm" data-testid="woo-reconcile-include-detected-wrapper">
                  <Checkbox
                    checked={wooReconcileIncludeDetected}
                    onCheckedChange={(checked) => setWooReconcileIncludeDetected(Boolean(checked))}
                    data-testid="woo-reconcile-include-detected-checkbox"
                  />
                  <span data-testid="woo-reconcile-include-detected-label">Include detected mismatches</span>
                </label>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground" data-testid="woo-reconcile-scope-label">Mismatch scope</p>
                    <Select value={wooReconcileScope} onValueChange={setWooReconcileScope}>
                      <SelectTrigger data-testid="woo-reconcile-scope-select">
                        <SelectValue placeholder="Select scope" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All mismatches (historical + active)</SelectItem>
                        <SelectItem value="active">Active statuses only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground" data-testid="woo-reconcile-max-orders-label">Max orders to evaluate</p>
                    <Input
                      value={wooReconcileMaxOrders}
                      onChange={(event) => setWooReconcileMaxOrders(event.target.value)}
                      type="number"
                      min={1}
                      max={500}
                      data-testid="woo-reconcile-max-orders-input"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={handleWooReconcileDryRun}
                    disabled={wooReconcileLoading || wooReconcileApplying}
                    data-testid="woo-reconcile-dry-run-button"
                  >
                    {wooReconcileLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Dry Run
                  </Button>
                  <Button
                    onClick={handleWooReconcileApply}
                    disabled={wooReconcileApplying || wooReconcileLoading}
                    data-testid="woo-reconcile-apply-button"
                  >
                    {wooReconcileApplying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Apply
                  </Button>
                </div>
              </div>
            </div>

            {wooReconcileResult && (
              <div className="space-y-3" data-testid="woo-reconcile-result-wrapper">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Card className="border" data-testid="woo-reconcile-stat-issues-card">
                    <CardContent className="p-3">
                      <p className="text-xs text-muted-foreground">Orders with issues</p>
                      <p className="text-lg font-semibold" data-testid="woo-reconcile-stat-issues-value">{wooReconcileResult.orders_with_issues || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="border" data-testid="woo-reconcile-stat-changes-card">
                    <CardContent className="p-3">
                      <p className="text-xs text-muted-foreground">Orders needing updates</p>
                      <p className="text-lg font-semibold text-amber-700" data-testid="woo-reconcile-stat-changes-value">
                        {wooReconcileResult.orders_with_changes ?? wooReconcileResult.orders_updated ?? 0}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border" data-testid="woo-reconcile-stat-lines-card">
                    <CardContent className="p-3">
                      <p className="text-xs text-muted-foreground">Line items changed</p>
                      <p className="text-lg font-semibold" data-testid="woo-reconcile-stat-lines-value">
                        {wooReconcileResult.line_items_with_changes ?? wooReconcileResult.line_items_updated ?? 0}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border" data-testid="woo-reconcile-stat-risky-card">
                    <CardContent className="p-3">
                      <p className="text-xs text-muted-foreground">Risky mappings</p>
                      <p className="text-lg font-semibold" data-testid="woo-reconcile-stat-risky-value">
                        {wooReconcileResult?.totals?.risky_fields || 0}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {(wooReconcileResult.warnings || []).length > 0 && (
                  <div className="rounded-md border border-amber-300 bg-amber-50/60 p-3" data-testid="woo-reconcile-warnings-panel">
                    <p className="text-xs font-medium text-amber-900">Warnings</p>
                    <ul className="list-disc ml-4 mt-1 text-xs text-amber-900" data-testid="woo-reconcile-warnings-list">
                      {(wooReconcileResult.warnings || []).map((warning, index) => (
                        <li key={`woo-reconcile-warning-${index}`} data-testid={`woo-reconcile-warning-${index}`}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="max-h-[320px] overflow-y-auto rounded-md border" data-testid="woo-reconcile-orders-list">
                  {(wooReconcileResult.orders || []).length === 0 ? (
                    <p className="p-3 text-sm text-muted-foreground" data-testid="woo-reconcile-orders-empty">
                      No mismatches found for the selected scope.
                    </p>
                  ) : (
                    <div className="divide-y">
                      {(wooReconcileResult.orders || []).map((order) => (
                        <div key={order.order_id || order.order_number} className="p-3 space-y-2" data-testid={`woo-reconcile-order-${order.order_number}`}>
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-medium" data-testid={`woo-reconcile-order-number-${order.order_number}`}>{order.order_number}</p>
                            <Badge variant="outline" data-testid={`woo-reconcile-order-status-${order.order_number}`}>{order.status || '—'}</Badge>
                            {order.has_changes ? (
                              <Badge className="bg-amber-600/90 text-white" data-testid={`woo-reconcile-order-needs-update-${order.order_number}`}>Needs update</Badge>
                            ) : (
                              <Badge variant="outline" className="text-emerald-700 border-emerald-300" data-testid={`woo-reconcile-order-no-update-${order.order_number}`}>No update</Badge>
                            )}
                            <span className="text-xs text-muted-foreground" data-testid={`woo-reconcile-order-issue-count-${order.order_number}`}>
                              Issues: {order.issue_count || 0}
                            </span>
                          </div>

                          <div className="space-y-1" data-testid={`woo-reconcile-order-lines-${order.order_number}`}>
                            {(order.line_items || [])
                              .filter((lineItem) => lineItem.needs_update || (lineItem.risky_mapping_diagnostics || []).length > 0)
                              .slice(0, 8)
                              .map((lineItem) => {
                                const lineItemKey = buildWooReconcileLineItemKey(order.order_number, lineItem);
                                const isExpanded = Boolean(wooReconcileExpandedLineItems[lineItemKey]);
                                const riskyMappings = lineItem.risky_mapping_diagnostics || [];
                                const lineItemDiffs = lineItem.diffs || [];
                                const unmappedFields = lineItem.unmapped_fields || [];

                                return (
                                  <div key={`${order.order_number}-${lineItem.line_item_id}-${lineItem.source_wc_line_item_id}`} className="rounded border p-2 text-xs" data-testid={`woo-reconcile-line-item-${order.order_number}-${lineItem.line_item_id}`}>
                                    <div className="flex items-start justify-between gap-2">
                                      <div>
                                        <p className="font-medium" data-testid={`woo-reconcile-line-item-title-${order.order_number}-${lineItem.line_item_id}`}>
                                          {lineItem.line_item_ref || lineItem.line_item_id || 'Line item'} · {lineItem.product_name || lineItem.woo_line_item_name || '—'}
                                        </p>
                                        <p className="text-muted-foreground" data-testid={`woo-reconcile-line-item-summary-${order.order_number}-${lineItem.line_item_id}`}>
                                          Changes: {lineItem.change_count || 0} · Risky mappings: {riskyMappings.length}
                                        </p>
                                      </div>

                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => toggleWooReconcileLineItemExpand(order.order_number, lineItem)}
                                        data-testid={`woo-reconcile-line-item-expand-button-${order.order_number}-${lineItem.line_item_id}`}
                                      >
                                        {isExpanded ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                                        {isExpanded ? 'Hide details' : 'Show details'}
                                      </Button>
                                    </div>

                                    {isExpanded && (
                                      <div className="mt-2 space-y-2 border-t pt-2" data-testid={`woo-reconcile-line-item-details-${order.order_number}-${lineItem.line_item_id}`}>
                                        <div>
                                          <p className="text-[11px] font-semibold text-slate-700">Risky mappings</p>
                                          {riskyMappings.length === 0 ? (
                                            <p className="text-[11px] text-muted-foreground" data-testid={`woo-reconcile-line-item-risky-empty-${order.order_number}-${lineItem.line_item_id}`}>
                                              No risky mappings for this line item.
                                            </p>
                                          ) : (
                                            <ul className="list-disc ml-4 mt-1 text-[11px] text-slate-700" data-testid={`woo-reconcile-line-item-risky-list-${order.order_number}-${lineItem.line_item_id}`}>
                                              {riskyMappings.map((risk, index) => (
                                                <li key={`${lineItemKey}-risk-${index}`} data-testid={`woo-reconcile-line-item-risky-row-${order.order_number}-${lineItem.line_item_id}-${index}`}>
                                                  {risk.field_label || 'Field'} → {risk.mapped_option_name || 'Unknown option'}
                                                  {risk.confidence ? ` (${risk.confidence})` : ''}
                                                </li>
                                              ))}
                                            </ul>
                                          )}
                                        </div>

                                        <div>
                                          <p className="text-[11px] font-semibold text-slate-700">Detected changes</p>
                                          {lineItemDiffs.length === 0 ? (
                                            <p className="text-[11px] text-muted-foreground" data-testid={`woo-reconcile-line-item-diff-empty-${order.order_number}-${lineItem.line_item_id}`}>
                                              No field-level customization changes.
                                            </p>
                                          ) : (
                                            <ul className="list-disc ml-4 mt-1 text-[11px] text-slate-700" data-testid={`woo-reconcile-line-item-diff-list-${order.order_number}-${lineItem.line_item_id}`}>
                                              {lineItemDiffs.slice(0, 12).map((diff, index) => (
                                                <li key={`${lineItemKey}-diff-${index}`} data-testid={`woo-reconcile-line-item-diff-row-${order.order_number}-${lineItem.line_item_id}-${index}`}>
                                                  [{diff.change_type}] {diff.old_option_name || diff.new_option_name || diff.field_key}
                                                </li>
                                              ))}
                                            </ul>
                                          )}
                                        </div>

                                        {unmappedFields.length > 0 && (
                                          <div>
                                            <p className="text-[11px] font-semibold text-red-700">Unmapped Woo fields</p>
                                            <ul className="list-disc ml-4 mt-1 text-[11px] text-red-700" data-testid={`woo-reconcile-line-item-unmapped-list-${order.order_number}-${lineItem.line_item_id}`}>
                                              {unmappedFields.map((field, index) => (
                                                <li key={`${lineItemKey}-unmapped-${index}`} data-testid={`woo-reconcile-line-item-unmapped-row-${order.order_number}-${lineItem.line_item_id}-${index}`}>
                                                  {field.field_label || field.field_ref}
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="border-border bg-card shadow-sm rounded-xl">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="order-search-input"
                placeholder="Search by order number or customer..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
                className="pl-10 h-10 bg-secondary/50"
              />
            </div>
            
            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="status-filter" className="w-[160px] h-10 bg-secondary/50">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Status</SelectItem>
                {Object.entries(statusLabels).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Store Filter */}
            <Select value={storeFilter} onValueChange={setStoreFilter}>
              <SelectTrigger data-testid="store-filter" className="w-[140px] h-10 bg-secondary/50">
                <SelectValue placeholder="All Stores" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Stores</SelectItem>
                {stores.map((store) => (
                  <SelectItem key={store.store_id} value={store.store_id}>
                    {store.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={dataTierFilter} onValueChange={setDataTierFilter}>
              <SelectTrigger data-testid="orders-data-tier-filter" className="w-[140px] h-10 bg-secondary/50">
                <SelectValue placeholder="Data Tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hot">Hot Data</SelectItem>
                <SelectItem value="cold">Cold Data</SelectItem>
              </SelectContent>
            </Select>

            <Select value={orderDateRangeFilter} onValueChange={setOrderDateRangeFilter}>
              <SelectTrigger data-testid="orders-date-range-filter" className="w-[200px] h-10 bg-secondary/50">
                <SelectValue placeholder="Order Date Range" />
              </SelectTrigger>
              <SelectContent>
                {ORDER_DATE_RANGE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {orderDateRangeFilter === 'custom' && (
              <div className="flex items-center gap-2" data-testid="orders-custom-date-range-inputs">
                <Input
                  type="date"
                  value={orderDateFrom}
                  onChange={(e) => setOrderDateFrom(e.target.value)}
                  className="w-[150px] h-10 bg-secondary/50"
                  data-testid="orders-date-from-input"
                />
                <Input
                  type="date"
                  value={orderDateTo}
                  onChange={(e) => setOrderDateTo(e.target.value)}
                  className="w-[150px] h-10 bg-secondary/50"
                  data-testid="orders-date-to-input"
                />
              </div>
            )}

            {hasActiveFilters && (
              <Button variant="ghost" size="icon" onClick={clearFilters} className="h-10 w-10">
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card className="border-border bg-card shadow-sm rounded-xl">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <ShoppingBag className="w-12 h-12 text-muted-foreground mb-4" strokeWidth={1} />
              <h3 className="text-lg font-medium mb-1">No orders found</h3>
              <p className="text-muted-foreground text-sm mb-4">
                {hasActiveFilters ? 'Try adjusting your filters' : 'Create your first order to get started'}
              </p>
              {!hasActiveFilters && (
                <Button onClick={() => navigate('/orders/new')}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Order
                </Button>
              )}
            </div>
          ) : (
            <>
              <Table className="table-luxury">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">
                      <Checkbox
                        checked={selectedOrders.length === orders.length && orders.length > 0}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead>
                      <SortButton field="order_number" onSort={handleSort} activeField={sortBy}>Order #</SortButton>
                    </TableHead>
                    <TableHead>
                      <SortButton field="customer_name" onSort={handleSort} activeField={sortBy}>Customer</SortButton>
                    </TableHead>
                    <TableHead>
                      <SortButton field="client_final_date" onSort={handleSort} activeField={sortBy}>Deadlines</SortButton>
                    </TableHead>
                    <TableHead>
                      <SortButton field="order_value" onSort={handleSort} activeField={sortBy}>Value</SortButton>
                    </TableHead>
                    <TableHead>Balance</TableHead>
                    <TableHead>Store</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow 
                      key={order.order_id} 
                      className={`table-row-hover ${selectedOrders.includes(order.order_id) ? 'bg-primary/5' : ''}`}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedOrders.includes(order.order_id)}
                          onCheckedChange={() => toggleSelectOrder(order.order_id)}
                          data-testid={`select-order-${order.order_id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div>
                            <a
                              href={`/orders/${order.order_id}/edit`}
                              className="font-mono text-sm font-medium underline underline-offset-2 hover:text-primary transition-colors"
                              data-testid={`order-number-link-${order.order_id}`}
                            >
                              {order.order_number}
                            </a>
                            <p className="text-xs text-muted-foreground">
                              {formatDateWord(order.order_date)}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleCopyOrderNumber(order)}
                            data-testid={`copy-order-number-button-${order.order_id}`}
                            title="Copy order number"
                          >
                            {copiedOrderId === order.order_id ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                          </Button>
                          {/* Group Order Indicators */}
                          <TooltipProvider>
                            {order.is_group_parent && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">
                                    <Users className="w-3 h-3" />
                                    <span className="text-xs font-medium">{order.child_orders_count || 0}</span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Parent Order: {order.group_name || 'Group'}</p>
                                  <p className="text-xs text-muted-foreground">{order.child_orders_count || 0} child order(s)</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {order.parent_order_id && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">
                                    <Link2 className="w-3 h-3" />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Child Order</p>
                                  <p className="text-xs text-muted-foreground">Part of: {order.parent_order_number || 'Group Order'}</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </TooltipProvider>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{order.customer_name || '—'}</p>
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <p className="text-xs text-muted-foreground" data-testid={`order-customer-no-${order.order_id}`}>
                              Customer No: {order.customer_no ?? '—'}
                            </p>
                            {order.customer_id && (
                              <Button
                                variant="ghost"
                                size="icon"
                                asChild
                                className="h-6 w-6 text-muted-foreground hover:text-primary"
                              >
                                <a
                                  href={`/fitting-studio/${order.customer_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label={`Open fitting studio for customer ${order.customer_no ?? order.customer_name ?? order.customer_id}`}
                                  title="Open fitting studio in new tab"
                                  data-testid={`orders-open-fitting-studio-${order.order_id}`}
                                >
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </a>
                              </Button>
                            )}
                          </div>
                          {order.customer_email && (
                            <p className="text-xs text-muted-foreground">{order.customer_email}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs space-y-0.5">
                          <p className="text-muted-foreground">
                            <span className="font-medium text-foreground">Client:</span>{' '}
                            {order.client_final_date 
                              ? formatDateWord(order.client_final_date)
                              : '—'
                            }
                          </p>
                          <p className="text-muted-foreground">
                            <span className="font-medium text-amber-500">Internal:</span>{' '}
                            {order.internal_delivery_date 
                              ? formatDateWord(order.internal_delivery_date)
                              : '—'
                            }
                          </p>
                          {order.test_fit_date && (
                            <p className="text-muted-foreground">
                              <span className="font-medium text-blue-500">Test Fit:</span>{' '}
                              {formatDateWord(order.test_fit_date)}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{formatCurrency(order.order_value)}</TableCell>
                      <TableCell className={order.balance_amount > 0 ? 'text-amber-400' : 'text-emerald-400'}>
                        {formatCurrency(order.balance_amount)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{formatStoreName(order.store) || '—'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={statusColors[order.status] || ''}>
                          {statusLabels[order.status] || order.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openNotesDialog(order)}
                          data-testid={`order-notes-button-${order.order_id}`}
                          title="View order notes"
                        >
                          <FileText className={`w-4 h-4 ${order.notes ? 'text-primary' : 'text-muted-foreground'}`} />
                        </Button>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`order-actions-${order.order_id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/orders/${order.order_id}/edit`)}>
                              <Edit2 className="w-4 h-4 mr-2" />
                              Edit Order
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleStatusChange(order.order_id, 'processing')}>
                              Mark Processing
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleStatusChange(order.order_id, 'pending_payment')}>
                              Mark Pending Payment
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleStatusChange(order.order_id, 'completed')}>
                              Mark Completed
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleDelete(order.order_id)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between p-4 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalOrders)} of {totalOrders} orders
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm px-3">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={notesDialogOpen} onOpenChange={setNotesDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="order-notes-dialog">
          <DialogHeader>
            <DialogTitle data-testid="order-notes-dialog-title">
              Order Notes {notesDialogOrder?.order_number ? `· ${notesDialogOrder.order_number}` : ''}
            </DialogTitle>
            <DialogDescription>
              Quick view of notes for this order.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground" data-testid="order-notes-customer-summary">
              {notesDialogOrder?.customer_name || '—'}
              {notesDialogOrder?.customer_no != null ? ` · Customer No ${notesDialogOrder.customer_no}` : ''}
            </div>
            <div className="rounded-md border bg-muted/30 p-3 min-h-[120px] whitespace-pre-wrap text-sm" data-testid="order-notes-content">
              {notesDialogOrder?.notes?.trim() ? notesDialogOrder.notes : 'No notes available for this order.'}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotesDialogOpen(false)} data-testid="order-notes-close-button">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WooCommerce Import Modal */}
      <Dialog open={wooImportOpen} onOpenChange={setWooImportOpen}>
        <DialogContent className="max-w-6xl" data-testid="woo-import-modal">
          <DialogHeader>
            <DialogTitle>Import from WooCommerce</DialogTitle>
            <DialogDescription>
              Fetch Woo orders by From/To date, configure status mapping, map incoming fields, and batch import selected orders.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border">
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle className="text-sm">Woo Orders</CardTitle>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span data-testid="woo-selected-orders-count">{wooSelectedOrderIds.length} selected</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={selectAllVisibleWooOrders}
                      data-testid="woo-select-all-orders-button"
                    >
                      Select all
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearWooOrderSelection}
                      data-testid="woo-clear-selected-orders-button"
                    >
                      Clear
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                  <Input
                    value={wooSearchTerm}
                    onChange={(e) => setWooSearchTerm(e.target.value)}
                    placeholder="Search Woo order ID / number / customer"
                    data-testid="woo-search-input"
                  />
                  <Select value={wooStatusFilter} onValueChange={setWooStatusFilter}>
                    <SelectTrigger data-testid="woo-status-filter">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="processing">Processing</SelectItem>
                      <SelectItem value="on-hold">On Hold</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="refunded">Refunded</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="date"
                    value={wooDateFrom}
                    onChange={(event) => setWooDateFrom(event.target.value)}
                    data-testid="woo-date-from-input"
                  />
                  <Input
                    type="date"
                    value={wooDateTo}
                    onChange={(event) => setWooDateTo(event.target.value)}
                    data-testid="woo-date-to-input"
                  />
                  <Button variant="outline" onClick={fetchWooPreview} data-testid="woo-refresh-button">
                    {wooLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Fetch'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="max-h-[420px] overflow-y-auto space-y-2">
                {wooOrders.map(order => (
                  <div key={order.woo_order_id} className="border rounded-lg p-2" data-testid={`woo-order-row-${order.woo_order_id}`}>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={wooSelectedOrderIds.includes(order.woo_order_id)}
                        onCheckedChange={() => toggleWooOrderSelection(order.woo_order_id)}
                        data-testid={`woo-order-select-checkbox-${order.woo_order_id}`}
                      />
                      <button
                        className={`flex-1 text-left rounded-md p-2 hover:bg-muted/40 transition ${wooSelectedOrderId === order.woo_order_id ? 'border border-primary bg-primary/5' : 'border border-transparent'}`}
                        onClick={() => handleSelectWooOrder(order)}
                        data-testid={`woo-order-focus-button-${order.woo_order_id}`}
                        type="button"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <p className="text-sm font-medium">#{order.woo_order_number} · {order.invoice_number || 'No invoice'}</p>
                            <p className="text-xs text-muted-foreground">{order.customer_name || '—'} · {order.status}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {order.already_imported && (
                              <Badge variant="outline" className="text-xs border-amber-300 text-amber-700" data-testid={`woo-order-imported-badge-${order.woo_order_id}`}>
                                Imported
                              </Badge>
                            )}
                            {getUnmappedCountForOrder(order) > 0 && (
                              <Badge variant="outline" className="text-xs border-red-300 text-red-700" data-testid={`woo-order-unmapped-badge-${order.woo_order_id}`}>
                                {getUnmappedCountForOrder(order)} unmapped
                              </Badge>
                            )}
                            {getRiskyMappingCountForOrder(order) > 0 && (
                              <Badge variant="outline" className="text-xs border-amber-300 text-amber-700" data-testid={`woo-order-risky-badge-${order.woo_order_id}`}>
                                {getRiskyMappingCountForOrder(order)} risky
                              </Badge>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {order.line_items?.length || 0} line item(s) · Selected {(wooSelectedLineItemsByOrder[order.woo_order_id] || []).length} · {formatCurrency(order.total)}
                        </p>
                      </button>
                    </div>
                  </div>
                ))}
                {!wooLoading && wooOrders.length === 0 && (
                  <p className="text-sm text-muted-foreground" data-testid="woo-orders-empty">No Woo orders found.</p>
                )}
              </CardContent>
            </Card>

            <Card className="border">
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle className="text-sm">Line Item Selection & Mapping</CardTitle>
                  <label className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="woo-show-only-unmapped-toggle-wrapper">
                    <Checkbox
                      checked={wooShowOnlyUnmapped}
                      onCheckedChange={(checked) => setWooShowOnlyUnmapped(Boolean(checked))}
                      data-testid="woo-show-only-unmapped-toggle"
                    />
                    Show only unmapped fields
                  </label>
                </div>
                {stores.length > 1 && (
                  <div className="w-56">
                    <Select value={wooStoreSelection || '__none__'} onValueChange={(value) => setWooStoreSelection(value === '__none__' ? '' : value)}>
                      <SelectTrigger data-testid="woo-store-select">
                        <SelectValue placeholder="Select store" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Select store</SelectItem>
                        {stores.map(store => (
                          <SelectItem key={store.store_id} value={store.store_id}>{store.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="woo-status-mapping-grid">
                  {WOO_STATUS_OPTIONS.map((option) => (
                    <div key={option.value} className="grid grid-cols-[1fr,1fr] items-center gap-2" data-testid={`woo-status-map-row-${option.value}`}>
                      <p className="text-xs text-muted-foreground">{option.label}</p>
                      <Select
                        value={wooStatusMapping[option.value] || DEFAULT_WOO_STATUS_MAPPING[option.value]}
                        onValueChange={(value) => updateWooStatusMapping(option.value, value)}
                      >
                        <SelectTrigger className="h-8" data-testid={`woo-status-map-select-${option.value}`}>
                          <SelectValue placeholder="Internal status" />
                        </SelectTrigger>
                        <SelectContent>
                          {INTERNAL_STATUS_OPTIONS.map((internalOption) => (
                            <SelectItem key={`${option.value}-${internalOption.value}`} value={internalOption.value}>
                              {internalOption.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </CardHeader>
              <CardContent className="max-h-[420px] overflow-y-auto">
                {!selectedWooOrder ? (
                  <p className="text-sm text-muted-foreground" data-testid="woo-line-items-empty">Select an order to view line items.</p>
                ) : (
                  <div className="space-y-2">
                    {(selectedWooOrder.line_items || []).map(item => (
                      <div key={item.line_item_id} className="border rounded p-2 flex items-start gap-3" data-testid={`woo-line-item-${item.line_item_id}`}>
                        <Checkbox
                          checked={selectedWooOrderLineItemIds.includes(item.line_item_id)}
                          onCheckedChange={() => toggleWooLineItem(item.line_item_id)}
                          data-testid={`woo-line-item-checkbox-${item.line_item_id}`}
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{item.name}</p>
                          <p className="text-xs text-muted-foreground">
                            Qty: {item.quantity} · Garment: {item.inferred_garment_type} · Total: {formatCurrency(item.total)}
                          </p>
                          <p className="text-xs text-muted-foreground">Customizations: {item.customization_count || 0} · Product: {item.matched_product_name || 'Not mapped'}</p>

                          {(item.woo_fields || []).length > 0 && (
                            <div className="mt-2 space-y-2 border-t pt-2">
                              {(wooShowOnlyUnmapped ? getUnmappedWooFieldsForLineItem(item, selectedWooOrder.woo_order_id) : (item.woo_fields || [])).map(field => {
                                const selectedOptionId = getLineItemMapping(selectedWooOrder.woo_order_id, item.line_item_id)[field.field_ref] || '__unmapped__';
                                return (
                                  <div key={`${item.line_item_id}-${field.field_ref}`} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center" data-testid={`woo-field-map-row-${item.line_item_id}-${field.field_ref}`}>
                                    <div className="md:col-span-1">
                                      <p className="text-[11px] font-medium text-slate-700">{field.field_label}</p>
                                      <p className="text-[11px] text-slate-500 truncate">{field.value}</p>
                                    </div>
                                    <div className="md:col-span-2">
                                      <Select
                                        value={selectedOptionId}
                                        onValueChange={(value) => updateWooFieldMapping(item.line_item_id, field.field_ref, value)}
                                      >
                                        <SelectTrigger className="h-8 text-xs" data-testid={`woo-field-map-select-${item.line_item_id}-${field.field_ref}`}>
                                          <SelectValue placeholder="Map to internal field" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="__unmapped__">Select mapping</SelectItem>
                                          <SelectItem value="__imported_details__">Imported details (keep source field)</SelectItem>
                                          {(item.customization_options || []).map(option => (
                                            <SelectItem key={option.option_id} value={option.option_id}>
                                              {option.option_name}{option.is_required ? ' *' : ''}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                );
                              })}
                              {wooShowOnlyUnmapped && getUnmappedWooFieldsForLineItem(item, selectedWooOrder.woo_order_id).length === 0 && (
                                <p className="text-[11px] text-emerald-600" data-testid={`woo-line-item-all-mapped-${item.line_item_id}`}>
                                  All Woo fields are mapped.
                                </p>
                              )}
                            </div>
                          )}

                          {(() => {
                            const unmapped = getUnmappedWooFieldsForLineItem(item, selectedWooOrder.woo_order_id);
                            if (unmapped.length === 0) return null;
                            return (
                              <p className="text-[11px] text-red-600 mt-1" data-testid={`woo-line-item-unmapped-${item.line_item_id}`}>
                                Unmapped incoming Woo fields: {unmapped.map(field => field.field_label).join(', ')}
                              </p>
                            );
                          })()}

                          {(() => {
                            const risky = getRiskyWooFieldsForLineItem(item);
                            if (risky.length === 0) return null;
                            return (
                              <p className="text-[11px] text-amber-700 mt-1" data-testid={`woo-line-item-risky-${item.line_item_id}`}>
                                Potentially incorrect auto-mappings detected: {risky.map(field => field.field_label).join(', ')}
                              </p>
                            );
                          })()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setWooImportOpen(false)} data-testid="woo-import-cancel-button">Cancel</Button>
            <Button
              onClick={openWooImportSummary}
              disabled={wooImporting || wooSelectedOrderIds.length === 0 || (stores.length > 1 && !wooStoreSelection)}
              data-testid="woo-import-confirm-button"
            >
              <DownloadCloud className="w-4 h-4 mr-2" />
              Review Summary ({wooSelectedOrderIds.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={wooSummaryModalOpen} onOpenChange={setWooSummaryModalOpen}>
        <DialogContent className="max-w-3xl" data-testid="woo-import-summary-modal">
          <DialogHeader>
            <DialogTitle data-testid="woo-import-summary-title">Woo Import Summary</DialogTitle>
            <DialogDescription>
              Review how many selected orders can be imported, and which ones will be skipped with reasons.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Card className="border" data-testid="woo-summary-selected-card">
              <CardContent className="p-3">
                <p className="text-xs text-muted-foreground">Selected Orders</p>
                <p className="text-xl font-semibold">{wooImportSummary?.selected_count || 0}</p>
              </CardContent>
            </Card>
            <Card className="border" data-testid="woo-summary-importable-card">
              <CardContent className="p-3">
                <p className="text-xs text-muted-foreground">Ready to Import</p>
                <p className="text-xl font-semibold text-emerald-700">{wooImportSummary?.importable_count || 0}</p>
              </CardContent>
            </Card>
            <Card className="border" data-testid="woo-summary-skipped-card">
              <CardContent className="p-3">
                <p className="text-xs text-muted-foreground">Will be Skipped</p>
                <p className="text-xl font-semibold text-amber-700">{wooImportSummary?.skipped_count || 0}</p>
              </CardContent>
            </Card>
          </div>

          {(wooImportSummary?.warning_count || 0) > 0 && (
            <div className="border rounded-md p-3 bg-amber-50/60" data-testid="woo-summary-warning-list">
              <p className="text-sm font-medium text-amber-800 mb-2">Review warnings before import</p>
              <div className="space-y-2">
                {(wooImportSummary?.warning_orders || []).map((order) => (
                  <div key={order.woo_order_id} className="text-xs text-amber-900" data-testid={`woo-summary-warning-order-${order.woo_order_id}`}>
                    <span className="font-medium">#{order.woo_order_number} · {order.invoice_number || 'No invoice'}:</span>{' '}
                    {(order.reasons || []).map(reason => reason.detail).join(' · ')}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="border rounded-md max-h-64 overflow-y-auto p-3" data-testid="woo-summary-skipped-list">
            {(wooImportSummary?.skipped_orders || []).length === 0 ? (
              <p className="text-sm text-emerald-700" data-testid="woo-summary-no-skips">No skip conditions detected.</p>
            ) : (
              <div className="space-y-3">
                {(wooImportSummary?.skipped_orders || []).map((order) => (
                  <div key={order.woo_order_id} className="border rounded-md p-2" data-testid={`woo-summary-skipped-order-${order.woo_order_id}`}>
                    <p className="text-sm font-medium">#{order.woo_order_number} · {order.invoice_number || 'No invoice'}</p>
                    <ul className="list-disc ml-4 mt-1 text-xs text-muted-foreground">
                      {(order.reasons || []).map((reason, index) => (
                        <li key={`${order.woo_order_id}-${reason.code}-${index}`}>{reason.detail}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setWooSummaryModalOpen(false)} data-testid="woo-summary-close-button">
              Back
            </Button>
            <Button
              onClick={handleImportWooOrder}
              disabled={wooImporting || (wooImportSummary?.importable_count || 0) === 0}
              data-testid="woo-summary-proceed-import-button"
            >
              {wooImporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DownloadCloud className="w-4 h-4 mr-2" />}
              Proceed Import ({wooImportSummary?.importable_count || 0})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedOrders.length} orders?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the selected orders
              and all associated garments, payments, and other records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={customerRepairDialogOpen} onOpenChange={setCustomerRepairDialogOpen}>
        <AlertDialogContent data-testid="orders-customer-repair-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="orders-customer-repair-dialog-title">Repair customer data across orders?</AlertDialogTitle>
            <AlertDialogDescription data-testid="orders-customer-repair-dialog-description">
              This will sync Orders/Garments/Fabric Orders from All Clients (master source).
              Type <span className="font-semibold">REPAIR</span> to continue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={customerRepairConfirmText}
            onChange={(e) => setCustomerRepairConfirmText(e.target.value)}
            placeholder="Type REPAIR"
            data-testid="orders-customer-repair-confirm-input"
          />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="orders-customer-repair-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRunCustomerMasterRepair}
              disabled={customerRepairConfirmText.trim() !== 'REPAIR' || customerRepairRunning}
              data-testid="orders-customer-repair-confirm-button"
            >
              {customerRepairRunning ? 'Repairing...' : 'Run Repair'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OrdersPage;
